import { readJson } from "../lib/content.server.js";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import StateMap from "../components/StateMap/StateMap.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import { surfaceFor } from "../lib/surface.js";
import styles from "./clients.module.css";
import { LOGO_VERSION, withVersion } from "../lib/assetVersions.js";

const PAGE = {
  title: "Our Clients",
  description:
    "State DOTs, cities, transit agencies and engineering firms that work with RDV Systems.",
  path: "/about/clients",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Clients", path: PAGE.path },
];
// `short` names the group in the hero's figures.
const GROUPS = [
  { type: "dot", title: "State departments of transportation", short: "state DOTs" },
  { type: "agency", title: "Counties, transit and public agencies", short: "agencies" },
  { type: "city", title: "Cities and towns", short: "cities and towns" },
  { type: "firm", title: "Engineering firms", short: "engineering firms" },
];

// The one switch for this page's light sections: these sit on the light "plan sheet"
// surface from app/styles/surfaces.css. Empty the list to put the whole page back on the
// dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["agency", "firm"];
const surface = surfaceFor(LIGHT_SECTIONS);

export function meta() {
  return buildMeta({ ...PAGE, jsonLd: [breadcrumbJsonLd(CRUMBS)] });
}

export async function loader() {
  return { clients: readJson("clients") };
}

export default function Clients({ loaderData }) {
  const { clients } = loaderData;
  const of = (type) =>
    clients.filter((c) => c.type === type).sort((a, b) => a.name.localeCompare(b.name));
  const states = new Set(clients.map((c) => c.state).filter(Boolean)).size;
  return (
    <>
      <section className={styles.hero} aria-labelledby="clients-title">
        <div className={`container ${styles.heroGrid}`}>
          <div className={styles.heroText}>
            <Breadcrumbs items={CRUMBS} />
            <h1 id="clients-title" className={styles.heroTitle}>
              Trusted by {of("dot").length} state DOTs
            </h1>
            <p className={styles.heroLead}>
              And by the counties, transit and airport authorities, cities and engineering firms
              around them, in {states} states.
            </p>
            <ul className={styles.figures} aria-label="Clients by type">
              {GROUPS.map((g) => (
                <li key={g.type}>
                  <strong>{of(g.type).length}</strong> {g.short}
                </li>
              ))}
            </ul>
          </div>
          <StateMap clients={clients} />
        </div>
      </section>

      {GROUPS.map((g) => {
        const items = of(g.type);
        if (!items.length) return null;
        return (
          <section
            key={g.type}
            className="section"
            data-surface={surface(g.type)}
            aria-labelledby={`clients-${g.type}`}
          >
            <div className="container">
              <SectionHeader id={`clients-${g.type}`} title={g.title} />
              <ul className={styles.logos}>
                {items.map((c) => (
                  <li key={c.logo} className={styles.client}>
                    <picture className={styles.mark}>
                      <source
                        type="image/webp"
                        srcSet={withVersion(`${c.logo}-mono.webp`, LOGO_VERSION)}
                      />
                      {/* The name is written under it, so the logo itself says nothing. */}
                      <img
                        src={withVersion(`${c.logo}-mono.png`, LOGO_VERSION)}
                        alt=""
                        height="64"
                        loading="lazy"
                        className={styles.logo}
                      />
                    </picture>
                    <span className={styles.name}>{c.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        );
      })}

      <CTABand
        variant="statement"
        image="/images/site/123bim/tour-gateway"
        title="Put your state on the map"
        text="Tell us about one corridor or one design, and we'll show you what we'd do with it."
        secondary={{ to: "/contact", label: "Contact us" }}
      />
    </>
  );
}
